import { Market, MarketType, ProbabilityHistory, Selection, SelectionStatus } from '../types/marketTypes';
import { Event, TtScore } from '../types/types';
import { compareMatchScores, matchScoreLabel, parseMatchScore, TtMatchScore } from './ttMatchScore';

/** One market section: a heading and the markets shown as cards under it. */
export interface MarketGroup {
  title: string;
  markets: Market[];
  /** The set the section holds, or undefined for the match-level one. */
  set?: number;
  /** Whether the section starts expanded, for a caller that renders it collapsible. */
  defaultOpen: boolean;
}

/**
 * One row of a market's flat selection list: the pick, and what to call it when its own name is not what
 * a trader should read. A market whose picks are named well enough by the backend leaves the label off.
 */
export interface SelectionRow {
  selection: Selection;
  label?: string;
}

/** One line of a market offering several at once: the two picks on it, and how it is labelled. */
export interface SelectionLine {
  /** The line itself — a handicap, a point target or a total — which is what orders the lines. */
  value: number;
  label: string;
  /** The market the line came from; what labels its two picks follows from it. */
  marketType: MarketType;
  selections: Selection[];
}

// Card titles for the markets the match as a whole settles.
const MATCH_CARD_TITLES: Partial<Record<MarketType, string>> = {
  [MarketType.TT_MATCH_WINNER]: 'Match Winner',
  [MarketType.TT_MATCH_TOTAL_ODD_EVEN]: 'Match Total Points Odd/Even',
  [MarketType.TT_MATCH_SETS_HANDICAP]: 'Match Sets Handicap',
  [MarketType.TT_MATCH_TOTAL_POINTS]: 'Match Total Points',
  [MarketType.TT_MATCH_CORRECT_SCORE]: 'Match Correct Score',
  [MarketType.TENNIS_MATCH_WINNER]: 'Match Winner',
  [MarketType.TENNIS_MATCH_RACE_TO_N]: 'Match Race to N Games',
  [MarketType.TENNIS_MATCH_GAMES_HANDICAP]: 'Match Games Handicap',
  [MarketType.TENNIS_MATCH_TOTAL_GAMES]: 'Match Total Games',
  [MarketType.TENNIS_MATCH_TOTAL_ODD_EVEN]: 'Match Total Games Odd/Even',
};

/**
 * What a per-set card is called once its set is named. Every one of these is titled `Set 2 <name>`,
 * the set being the only thing telling an event's cards of one such type apart, so the set is prefixed
 * once here rather than spelled out per type.
 */
const TT_SET_CARD_TITLES: Partial<Record<MarketType, string>> = {
  [MarketType.TT_SET_WINNER]: 'Winner',
  [MarketType.TT_SET_POINTS_HANDICAP]: 'Points Handicap',
  [MarketType.TT_SET_TOTAL_POINTS]: 'Total Points',
  [MarketType.TT_SET_TOTAL_ODD_EVEN]: 'Total Points Odd/Even',
  [MarketType.TT_RACE_TO_N]: 'Race to N',
  [MarketType.TT_SET_POINT_N_WINNER]: 'Point N Winner',
  [MarketType.TENNIS_SET_GAME_WINNER]: 'Game Winner',
};

/**
 * The market types quoting a rolling total, whose two sides are Over and Under rather than the
 * competitors. They differ only in what they count — a table tennis set's points or match's points, a
 * tennis match's games — so everything about how their lines read is shared, and the match ones name no
 * set at all.
 */
const TOTAL_TYPES: MarketType[] = [
  MarketType.TT_SET_TOTAL_POINTS,
  MarketType.TT_MATCH_TOTAL_POINTS,
  MarketType.TENNIS_MATCH_TOTAL_GAMES,
];

/**
 * The market types quoted as an over/under pair, which the two-column line layout is for. Mirrors the
 * backend enum's own over/under flag, which the market DTO does not carry.
 */
const OVER_UNDER_TYPES: MarketType[] = [
  MarketType.INNING_RUNS,
  MarketType.TEAM_4S,
  MarketType.TEAM_6S,
  MarketType.TOTAL_4S,
  MarketType.TOTAL_6S,
  MarketType.MATCH_TOTAL_RUNS,
  MarketType.MATCH_TOTAL_WICKETS,
];

/** Sorts a market naming no number of its own last. */
const UNNUMBERED = Number.MAX_SAFE_INTEGER;

/** The set a market is settled by; markets naming no set sort last. */
const setNumber = (market: Market): number => {
  const parsed = parseInt(market.reference ?? '', 10);
  return isNaN(parsed) ? UNNUMBERED : parsed;
};

/**
 * Within a set: who wins it, then the markets on its point score — the margin, then the total, then the
 * total's parity right behind it — then the markets settled inside it, coarsest first. The same reading
 * as the match section, where the parity also follows the total it is the parity of: a section runs from
 * the whole set down to the single rally the point winner is decided by.
 */
const WITHIN_SET_ORDER: MarketType[] = [
  MarketType.TT_SET_WINNER,
  MarketType.TT_SET_POINTS_HANDICAP,
  MarketType.TT_SET_TOTAL_POINTS,
  MarketType.TT_SET_TOTAL_ODD_EVEN,
  MarketType.TT_RACE_TO_N,
  MarketType.TT_SET_POINT_N_WINNER,
];

const withinSet = (market: Market): number => {
  const order = WITHIN_SET_ORDER.indexOf(market.marketType);
  return order === -1 ? WITHIN_SET_ORDER.length : order;
};

/**
 * Title for a market card. Table Tennis stores enum-style names (`SET_1_WINNER`), so they are
 * relabelled here; a per-set market is titled by its set, the only thing telling it from its siblings.
 * The lines a market offers are not in the title — they live on its selections.
 */
export function marketCardTitle(market: Market): string {
  const perSet = TT_SET_CARD_TITLES[market.marketType];
  if (perSet) {
    // A market whose set cannot be read keeps the plain name: the card is an anomaly either way, and
    // a title saying `Set undefined` reads as a broken page rather than as a market missing its set.
    return market.reference ? `Set ${market.reference} ${perSet}` : setlessTitle(market.marketType, perSet);
  }
  return MATCH_CARD_TITLES[market.marketType] ?? market.name.replace(/^Innings Runs:\s*/, '');
}

/**
 * What a per-set card is called when it names no set. The set winner is the one type whose name is not
 * a noun on its own — `Winner` says nothing about what is won — so it keeps its scope in the title.
 */
const setlessTitle = (marketType: MarketType, name: string): string =>
  marketType === MarketType.TT_SET_WINNER ? `Set ${name}` : name;

/**
 * The lines of a Table Tennis market that offers several at once, in the order they are offered, or
 * null for a market whose selections are not lines. The two sides of a line have to be rendered
 * together: they are one pick, and each line carries its own status and result.
 *
 * <p>A selection whose line cannot be read makes the whole market fall back to a plain list, so a
 * reference in a format this does not know hides no selection.
 */
export function ttSelectionLines(market: Market): SelectionLine[] | null {
  const lineOf = lineReader(market.marketType);
  if (!lineOf || market.selections.length === 0) {
    return null;
  }

  const byValue = new Map<number, SelectionLine>();
  for (const selection of market.selections) {
    const value = lineOf(selection.reference ?? '');
    if (value === null) {
      return null;
    }
    const line = byValue.get(value);
    if (line) {
      line.selections.push(selection);
    } else {
      byValue.set(value, {
        value,
        label: lineLabel(market.marketType, value),
        marketType: market.marketType,
        selections: [selection],
      });
    }
  }

  const lines = Array.from(byValue.values()).sort((a, b) => a.value - b.value);
  if (TOTAL_TYPES.includes(market.marketType)) {
    // Over reads first whatever order the selections arrive in: a total's two sides are a direction, and
    // a pair that swapped sides between two lines of the same card would be read as a different bet.
    lines.forEach(line => line.selections.sort((a, b) => sideOrder(a) - sideOrder(b)));
  }
  return lines;
}

/** The two sides of a total, as its selection references name them and as they are shown. */
const TOTAL_SIDES: Record<string, string> = { OVER: 'Over', UNDER: 'Under' };

/** The side a total's selection picks, read off its reference, or null when it names none. */
function totalSide(selection: Selection): string | null {
  return TOTAL_SIDES[selection.reference?.split(':')[0] ?? ''] ?? null;
}

const sideOrder = (selection: Selection): number => (totalSide(selection) === TOTAL_SIDES.OVER ? 0 : 1);

/**
 * How one pick on a line is labelled, or undefined to label it by its own name. A total's selections are
 * named after the line its box is already headed by (`Over 18.5`), so only the side is shown — and the
 * side is read off the reference, never off the name: reading a market's sides off its selection names
 * is what once put a card with no selections at all on screen.
 */
export function linePickLabel(marketType: MarketType, selection: Selection): string | undefined {
  return TOTAL_TYPES.includes(marketType)
    ? totalSide(selection) ?? undefined
    : undefined;
}

/**
 * The line a market is quoting right now, or null when its lines do not name one. A rolling line leaves
 * every line it has moved off suspended, so the single open pair is the one being traded — while a
 * market that offers all its lines at once keeps several open and singles out none of them.
 */
export function quotedLine(lines: SelectionLine[]): SelectionLine | null {
  const open = lines.filter(line =>
    line.selections.some(selection => selection.status === SelectionStatus.OPEN));
  return lines.length > 1 && open.length === 1 ? open[0] : null;
}

/**
 * Reads one market type's line off a whole selection reference, or null for a type whose selections
 * carry none. Which side of the colon holds the line is the type's own business: a market whose picks
 * name a competitor states the line first, while a total states the side first, and a reader that
 * assumed the first segment would read `OVER` as an unreadable line and drop the whole market to a flat
 * list — losing the box per line, and with it the highlight that says which line is being quoted.
 */
function lineReader(marketType: MarketType): ((reference: string) => number | null) | null {
  switch (marketType) {
    case MarketType.TT_MATCH_SETS_HANDICAP:
    case MarketType.TT_SET_POINTS_HANDICAP:
    case MarketType.TENNIS_MATCH_GAMES_HANDICAP:
      // `line:competitorId`.
      return reference => halfLine(beforeColon(reference));
    case MarketType.TT_RACE_TO_N:
    case MarketType.TT_SET_POINT_N_WINNER:
    case MarketType.TENNIS_MATCH_RACE_TO_N:
    case MarketType.TENNIS_SET_GAME_WINNER:
      // `number:competitorId`, a whole number — the target of a race in points or games, or the point or
      // game a pick is on. One format, as the backend writes it; the types are told apart by the type, never here.
      return reference => wholeNumber(beforeColon(reference));
    case MarketType.TT_SET_TOTAL_POINTS:
    case MarketType.TT_MATCH_TOTAL_POINTS:
    case MarketType.TENNIS_MATCH_TOTAL_GAMES:
      // `OVER:18.5` — the side leads, since neither side of a total belongs to a competitor.
      return reference => halfLine(afterColon(reference));
    default:
      return null;
  }
}

const beforeColon = (reference: string): string => reference.split(':')[0];

const afterColon = (reference: string): string => reference.split(':')[1] ?? '';

/** A half line. A whole one would leave a push these markets have no outcome for. */
const halfLine = (text: string): number | null =>
  /^[-+]?\d+\.\d+$/.test(text) ? parseFloat(text) : null;

const wholeNumber = (text: string): number | null => (/^\d+$/.test(text) ? parseInt(text, 10) : null);

function lineLabel(marketType: MarketType, value: number): string {
  if (marketType === MarketType.TT_RACE_TO_N || marketType === MarketType.TENNIS_MATCH_RACE_TO_N) {
    return `Race to ${value}`;
  }
  if (marketType === MarketType.TT_SET_POINT_N_WINNER) {
    // The point of the set the pair is on, counting from its first rally.
    return `Point ${value}`;
  }
  if (marketType === MarketType.TENNIS_SET_GAME_WINNER) {
    // The game of the set the pair is on, counting from its first.
    return `Game ${value}`;
  }
  if (TOTAL_TYPES.includes(marketType)) {
    // Neither signed nor stated from a competitor's side: a total counts what both players score.
    return `Total ${value}`;
  }
  // Stated from the first competitor's point of view, as the market is sent and resulted.
  return `Line ${value > 0 ? '+' : ''}${value}`;
}

/**
 * The Table Tennis market sections in display order: the markets the match as a whole settles, then
 * one section per set holding everything that set settles. A set is the unit a trader watches, and it
 * is also the unit that goes void — a straight-sets win refunds a whole section at once.
 *
 * <p>`currentSet` only decides which section a collapsible caller starts expanded; every section is
 * returned either way.
 */
/** The set being played, read off the score history: the highest set any score names. */
export function ttCurrentSet(ttScores?: TtScore[]): number | undefined {
  const latest = (ttScores ?? []).reduce((set, score) => Math.max(set, score.currentSet), 0);
  return latest > 0 ? latest : undefined;
}

/** The tennis sections: the match markets in reading order, then one per set holding its games. */
export function tennisMarketSections(groupedMarkets: Record<string, Market[]>): MarketGroup[] {
  const of = (type: MarketType): Market[] => groupedMarkets[type] || [];

  const matchLevel = [
    ...of(MarketType.TENNIS_MATCH_WINNER),
    ...of(MarketType.TENNIS_MATCH_RACE_TO_N),
    ...of(MarketType.TENNIS_MATCH_GAMES_HANDICAP),
    ...of(MarketType.TENNIS_MATCH_TOTAL_GAMES),
    ...of(MarketType.TENNIS_MATCH_TOTAL_ODD_EVEN),
  ];
  const perSet = of(MarketType.TENNIS_SET_GAME_WINNER);

  const sections: MarketGroup[] = matchLevel.length > 0
    ? [{ title: 'Match', markets: matchLevel, defaultOpen: true }]
    : [];
  const sets = Array.from(new Set(perSet.map(setNumber))).sort((a, b) => a - b);
  sets.forEach(set => sections.push({
    title: set === UNNUMBERED ? 'Set' : `Set ${set}`,
    set: set === UNNUMBERED ? undefined : set,
    defaultOpen: true,
    markets: perSet.filter(market => setNumber(market) === set),
  }));
  return sections;
}

export function ttMarketSections(groupedMarkets: Record<string, Market[]>,
                                 currentSet?: number): MarketGroup[] {
  const of = (type: MarketType): Market[] => groupedMarkets[type] || [];

  // Read as a set section is: who takes it, then the margin, then the total — with the total's parity
  // right behind the total it is the parity of. Everything the sets decide comes before everything the
  // points decide, which is why the correct score sits with the sets handicap: the two read the same
  // distribution. The match total names no set, so it belongs here and not among the per-set markets,
  // which are grouped by the set their reference names.
  const matchLevel = [
    ...of(MarketType.TT_MATCH_WINNER),
    ...of(MarketType.TT_MATCH_SETS_HANDICAP),
    ...of(MarketType.TT_MATCH_CORRECT_SCORE),
    ...of(MarketType.TT_MATCH_TOTAL_POINTS),
    ...of(MarketType.TT_MATCH_TOTAL_ODD_EVEN),
  ];
  const perSet = [
    ...of(MarketType.TT_SET_WINNER),
    ...of(MarketType.TT_SET_POINTS_HANDICAP),
    ...of(MarketType.TT_SET_TOTAL_POINTS),
    ...of(MarketType.TT_SET_TOTAL_ODD_EVEN),
    ...of(MarketType.TT_RACE_TO_N),
    ...of(MarketType.TT_SET_POINT_N_WINNER),
  ];

  const sets = Array.from(new Set(perSet.map(setNumber))).sort((a, b) => a - b);

  const sections: MarketGroup[] = matchLevel.length > 0
    ? [{ title: 'Match', markets: matchLevel, defaultOpen: true }]
    : [];

  // The set being played is the one worth reading; before the first point that is the earliest offered.
  const openSet = currentSet ?? sets.find(set => set !== UNNUMBERED);

  sets.forEach(set => sections.push({
    title: set === UNNUMBERED ? 'Set' : `Set ${set}`,
    set: set === UNNUMBERED ? undefined : set,
    // A section naming no set holds an anomaly, so it is never the one tucked away.
    defaultOpen: set === UNNUMBERED || set === openSet,
    markets: perSet.filter(market => setNumber(market) === set).sort((a, b) => withinSet(a) - withinSet(b)),
  }));

  return sections;
}

/**
 * The rows of a market rendered as a flat list of picks, in the order they should be read. Every market
 * but one is read in the order the backend sent it, under the names it gave them; the correct score is
 * regrouped and relabelled, because four endings stated from one competitor's side do not read as a
 * book — see {@link matchScoreLabel}.
 *
 * <p>Fail-open, as {@link ttSelectionLines} is: anything that cannot be relabelled is returned exactly
 * as it arrived. A reference in a format this does not know, or an event whose competitors are not to
 * hand, must leave a card looking plain — never leave a pick that is taking bets off the screen.
 */
export function selectionRows(market: Market, event?: Event): SelectionRow[] {
  const asSent = (): SelectionRow[] => market.selections.map(selection => ({ selection }));
  if (market.marketType !== MarketType.TT_MATCH_CORRECT_SCORE) {
    return asSent();
  }
  return correctScoreRows(market.selections, event) ?? asSent();
}

/** One pick on the correct score, kept next to the ending it names while the picks are being ordered. */
interface ScoredSelection {
  selection: Selection;
  score: TtMatchScore;
}

/**
 * The four endings as picks: grouped by the player who takes them, named after that player, or null
 * when any of that is missing. All four are relabelled or none is — a card mixing `Petrov 2-0` with a
 * bare `1-2` would read as two different markets.
 */
function correctScoreRows(selections: Selection[], event?: Event): SelectionRow[] | null {
  const nameA = event?.firstCompetitor?.name;
  const nameB = event?.secondCompetitor?.name;
  if (!nameA || !nameB) {
    return null;
  }

  const scored: ScoredSelection[] = [];
  for (const selection of selections) {
    const score = parseMatchScore(selection.reference);
    if (!score) {
      return null;
    }
    scored.push({ selection, score });
  }

  return scored
    .sort((left, right) => compareMatchScores(left.score, right.score))
    .map(({ selection, score }) => ({ selection, label: matchScoreLabel(score, nameA, nameB) }));
}

/**
 * Whether the market is an over/under pair, decided by its type. Reading it off the selection names
 * instead would take any market with a competitor called "Saunders" for one, and the line the layout
 * then looks for is nowhere in a competitor name — leaving a card with no selections at all.
 */
export function isOverUnderMarket(market: Market): boolean {
  return OVER_UNDER_TYPES.includes(market.marketType);
}

/** A history entry that recorded something, so its probabilities can be read without a guard. */
export interface RecordedProbabilities extends ProbabilityHistory {
  probabilities: string;
}

/**
 * The probability snapshots actually recorded for a market. An entry is written whenever the market is
 * repriced, but carries nothing for a market type whose handler records no probabilities — so the empty
 * ones are dropped here rather than offered as a history that opens on nothing.
 */
export function recordedProbabilitiesHistory(market: Market): RecordedProbabilities[] {
  return (market.probabilitiesHistory ?? [])
    .filter((entry): entry is RecordedProbabilities => !!entry.probabilities);
}
